/**
 * Даты календаря: день — строка ГГГГ-ММ-ДД по часам телефона, неделя с понедельника.
 *
 * ПЕРЕХОД НА ЛЕТНЕЕ ВРЕМЯ. Сутки не всегда 24 часа, а местная полночь в некоторых поясах в день
 * перевода не существует. Поэтому дни считаем по календарю (год/месяц/день, середина дня),
 * а не прибавлением 86 400 000 мс, — иначе неделя «съезжала» бы на день раз в полгода.
 */

const pad = (n: number) => String(n).padStart(2, '0');

/** Местный день даты. */
export function dayKey(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** День → дата в середине местного дня (безопасно для арифметики). */
export function noonOf(key: string): Date {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d, 12, 0, 0, 0);
}

/** Начало местного дня (для границ запроса). */
export function startOf(key: string): Date {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d, 0, 0, 0, 0);
}

export function addDays(key: string, n: number): string {
  const d = noonOf(key);
  return dayKey(new Date(d.getFullYear(), d.getMonth(), d.getDate() + n, 12));
}

/** Разница в днях b − a (по календарю). */
export function daysBetween(a: string, b: string): number {
  const [ya, ma, da] = a.split('-').map(Number);
  const [yb, mb, db] = b.split('-').map(Number);
  return Math.round((Date.UTC(yb, mb - 1, db) - Date.UTC(ya, ma - 1, da)) / 86_400_000);
}

/** День недели, понедельник = 0. */
export function weekdayMon(key: string): number {
  return (noonOf(key).getDay() + 6) % 7;
}

export function weekStart(key: string): string {
  return addDays(key, -weekdayMon(key));
}

/** Семь дней недели, в которую попадает день (пн–вс). */
export function weekDays(key: string): string[] {
  const s = weekStart(key);
  return Array.from({ length: 7 }, (_, i) => addDays(s, i));
}

export interface MonthCell { key: string; inMonth: boolean }

/** Сетка месяца: с понедельника до 1-го числа по воскресенье после последнего. */
export function monthGrid(key: string): MonthCell[] {
  const d = noonOf(key);
  const first = dayKey(new Date(d.getFullYear(), d.getMonth(), 1, 12));
  const last = dayKey(new Date(d.getFullYear(), d.getMonth() + 1, 0, 12));
  const start = weekStart(first);
  const end = addDays(last, 6 - weekdayMon(last));
  const out: MonthCell[] = [];
  const month = d.getMonth();
  for (let k = start; daysBetween(k, end) >= 0; k = addDays(k, 1)) {
    out.push({ key: k, inMonth: noonOf(k).getMonth() === month });
  }
  return out;
}

/** Предел выдачи календаря CRM (routes/calendar.ts → MAX_RANGE_DAYS). */
export const MAX_FEED_DAYS = 62;

/** Границы запроса выдачи для набора дней: [начало первого, начало дня после последнего). */
export function feedRange(days: string[]): { from: string; to: string } {
  const first = days[0];
  const last = days[days.length - 1];
  return { from: startOf(first).toISOString(), to: startOf(addDays(last, 1)).toISOString() };
}

/** Дни, которые видны сейчас: неделя или сетка месяца. */
export function visibleDays(selected: string, month: boolean): string[] {
  return month ? monthGrid(selected).map((c) => c.key) : weekDays(selected);
}

export const WD = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const MON = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
const MONG = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];

/** «Октябрь 2026». */
export function monthTitle(key: string): string {
  const d = noonOf(key);
  return `${MON[d.getMonth()]} ${d.getFullYear()}`;
}

/** «10 октября». */
export function dayMonth(key: string): string {
  const d = noonOf(key);
  return `${d.getDate()} ${MONG[d.getMonth()]}`;
}

/** Заголовок дня: «Сегодня», «Завтра», «Вчера», «Пт, 16 октября». */
export function dayTitle(key: string, today: string): string {
  const diff = daysBetween(today, key);
  if (diff === 0) return 'Сегодня';
  if (diff === 1) return 'Завтра';
  if (diff === -1) return 'Вчера';
  return `${WD[weekdayMon(key)]}, ${dayMonth(key)}`;
}

/** «сегодня» / «вчера» / «3 октября» — в строке «срок был …». */
export function dayWord(key: string, today: string): string {
  const diff = daysBetween(today, key);
  if (diff === 0) return 'сегодня';
  if (diff === -1) return 'вчера';
  if (diff === 1) return 'завтра';
  return dayMonth(key);
}

/** ЧЧ:ММ местного времени момента. */
export function hhmm(iso: string): string {
  const d = new Date(iso);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** День + ЧЧ:ММ → момент ISO (местное время). */
export function momentOf(key: string, time: string): string {
  const [y, m, d] = key.split('-').map(Number);
  const [h, mi] = time.split(':').map(Number);
  return new Date(y, m - 1, d, h, mi, 0, 0).toISOString();
}
